// Redaction: the single gate every diagnostic value passes through before it
// is stored or displayed. Anything that looks like a credential is dropped.

const SECRET_KEY_RE =
  /(authorization|auth[-_]?token|apikey|api[-_]?key|access[-_]?token|refresh[-_]?token|id[-_]?token|token_hash|service[-_]?role|token|secret|password|passcode|otp|pin|cvv|bearer|card|upi[-_]?pin|session)/i;

const PATTERNS: Array<[RegExp, string]> = [
  [/eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{4,}/g, "[jwt:redacted]"],
  [/sb_(secret|publishable)_[A-Za-z0-9_-]+/g, "[sb-key:redacted]"],
  [/(bearer)\s+[A-Za-z0-9._-]+/gi, "$1 [redacted]"],
  [/\b[A-Za-z0-9._-]{0,12}(sk|pk)_(live|test)_[A-Za-z0-9]+/g, "[key:redacted]"],
  [/([?&](access_token|refresh_token|token|token_hash|apikey|otp|code)=)[^&#\s]+/gi, "$1[redacted]"],
  [/\b(otp|code|pin)\b\s*[:=]?\s*\d{4,8}/gi, "$1 [redacted]"],
];

export function redactString(input: string): string {
  let out = String(input ?? "");
  for (const [re, replacement] of PATTERNS) out = out.replace(re, replacement);
  return out;
}

export function redactValue(value: unknown, depth = 0): unknown {
  if (value == null) return value;
  if (depth > 5) return "[depth-limit]";
  if (typeof value === "string") return redactString(value).slice(0, 2000);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (Array.isArray(value)) return value.slice(0, 25).map((v) => redactValue(v, depth + 1));
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (SECRET_KEY_RE.test(k)) {
        out[k] = "[redacted]";
        continue;
      }
      out[k] = redactValue(v, depth + 1);
    }
    return out;
  }
  return "[unsupported]";
}

export function redactObject(
  value: Record<string, unknown> | undefined | null,
): Record<string, unknown> {
  if (!value) return {};
  return redactValue(value) as Record<string, unknown>;
}

/** URLs are recorded, query credentials are not. */
export function redactUrl(url: string): string {
  try {
    const u = new URL(url, typeof location !== "undefined" ? location.href : "https://local");
    const secretParams = ["token", "access_token", "refresh_token", "token_hash", "apikey", "otp", "code", "key"];
    for (const p of secretParams) if (u.searchParams.has(p)) u.searchParams.set(p, "[redacted]");
    return u.toString();
  } catch {
    return redactString(url);
  }
}
